/**
 * 模块 → 真实源文件索引。
 *
 * 源码来自本机保存的君和展足前端工程副本，按 ?raw 懒加载，
 * 切到哪个模块才把那个模块的文件读进来，避免首屏把几十个源文件塞进包里。
 */

export type SourceDoc = {
  id: string;
  label: string;
  path: string;
  /** import.meta.glob 的键，形如 ./repo/gongzi__GongziPage.tsx.txt */
  key: string;
};

export type SystemModule = {
  id: string;
  label: string;
  hint: string;
  /** 开发环境走本地同源反代，便于监听 iframe 内的点击 */
  devPath: string;
  /** 生产环境直接访问内测地址（跨域，仅展示） */
  directUrl: string;
  primary: string;
  sources: SourceDoc[];
};

const REPO = import.meta.glob("./repo/*.txt", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

const cache = new Map<string, string>();

export async function readSource(doc: SourceDoc): Promise<string> {
  const cached = cache.get(doc.key);
  if (cached !== undefined) return cached;
  const loader = REPO[doc.key];
  if (!loader) return "";
  const text = await loader();
  cache.set(doc.key, text);
  return text;
}

function doc(module: string, file: string, label: string, path: string): SourceDoc {
  return { id: `${module}-${file}`, label, path, key: `./repo/${module}__${file}.txt` };
}

export const SYSTEM_MODULES: SystemModule[] = [
  {
    id: "gongzi",
    label: "工资表",
    hint: "工资录入、明细、统计、银行卡、设置",
    devPath: "/gongzibiao-app/?qianru=1",
    directUrl: "https://nc.jhzzbgxt.cn/gongzibiao-app/?qianru=1",
    primary: "gongzi-GongziPage.tsx",
    sources: [
      doc("gongzi", "GongziPage.tsx", "GongziPage.tsx", "src/pages/GongziPage.tsx"),
      doc("gongzi", "MingxiPage.tsx", "MingxiPage.tsx", "src/pages/MingxiPage.tsx"),
      doc("gongzi", "TongjiPage.tsx", "TongjiPage.tsx", "src/pages/TongjiPage.tsx"),
      doc("gongzi", "PeizhiPage.tsx", "PeizhiPage.tsx", "src/pages/PeizhiPage.tsx"),
      doc("gongzi", "ShezhiPage.tsx", "ShezhiPage.tsx", "src/pages/ShezhiPage.tsx"),
      doc("gongzi", "YinhangkaPage.tsx", "YinhangkaPage.tsx", "src/pages/YinhangkaPage.tsx"),
      doc("gongzi", "JinruPage.tsx", "JinruPage.tsx", "src/pages/JinruPage.tsx"),
      doc("gongzi", "Buju.tsx", "Buju.tsx", "src/components/Buju.tsx"),
      doc("gongzi", "App.tsx", "App.tsx", "src/App.tsx"),
      doc("gongzi", "api.ts", "api.ts", "src/api.ts"),
    ],
  },
  {
    id: "kaoqin",
    label: "考勤",
    hint: "打卡记录、排班、考勤统计",
    devPath: "/kaoqin-app/",
    directUrl: "https://nc.jhzzbgxt.cn/kaoqin-app/",
    primary: "kaoqin-KaoqinPage.tsx",
    sources: [
      doc("kaoqin", "KaoqinPage.tsx", "KaoqinPage.tsx", "src/pages/KaoqinPage.tsx"),
      doc("kaoqin", "query.get.ts", "query.get.ts", "server/routes/kaoqin/query.get.ts"),
      doc("kaoqin", "jichu.get.ts", "jichu.get.ts", "server/routes/kaoqin/jichu.get.ts"),
    ],
  },
  {
    id: "baobiao",
    label: "报表",
    hint: "录入、查询、对比、配置与图表",
    devPath: "/baobiao-app/",
    directUrl: "https://nc.jhzzbgxt.cn/baobiao-app/",
    primary: "baobiao-LuruPage.tsx",
    sources: [
      doc("baobiao", "LuruPage.tsx", "LuruPage.tsx", "src/pages/LuruPage.tsx"),
      doc("baobiao", "ChaxunPage.tsx", "ChaxunPage.tsx", "src/pages/ChaxunPage.tsx"),
      doc("baobiao", "DuibiPage.tsx", "DuibiPage.tsx", "src/pages/DuibiPage.tsx"),
      doc("baobiao", "PeizhiPage.tsx", "PeizhiPage.tsx", "src/pages/PeizhiPage.tsx"),
      doc("baobiao", "Buju.tsx", "Buju.tsx", "src/components/Buju.tsx"),
      doc("baobiao", "App.tsx", "App.tsx", "src/App.tsx"),
      doc("baobiao", "api.ts", "api.ts", "src/api.ts"),
    ],
  },
  {
    id: "cangcu",
    label: "仓储配送",
    hint: "采购、出入库、配送与操作日志",
    devPath: "/cangcu-app/",
    directUrl: "https://nc.jhzzbgxt.cn/cangcu-app/",
    primary: "cangcu-CaigouPage.tsx",
    sources: [
      doc("cangcu", "CaigouPage.tsx", "CaigouPage.tsx", "src/pages/CaigouPage.tsx"),
      doc("cangcu", "CangcuPage.tsx", "CangcuPage.tsx", "src/pages/CangcuPage.tsx"),
      doc("cangcu", "RizhiPage.tsx", "RizhiPage.tsx", "src/pages/RizhiPage.tsx"),
      doc("cangcu", "Buju.tsx", "Buju.tsx", "src/components/Buju.tsx"),
    ],
  },
  {
    id: "main",
    label: "管理系统",
    hint: "首页、日志、用户、门店、角色、供应商",
    devPath: "/nc-main/",
    directUrl: "https://nc.jhzzbgxt.cn/",
    primary: "main-HomePage.tsx",
    sources: [
      doc("main", "HomePage.tsx", "HomePage.tsx", "src/pages/HomePage.tsx"),
      doc("main", "YonghuPage.tsx", "YonghuPage.tsx", "src/pages/YonghuPage.tsx"),
      doc("main", "RizhiPage.tsx", "RizhiPage.tsx", "src/pages/RizhiPage.tsx"),
      doc("main", "LoginPage.tsx", "LoginPage.tsx", "src/pages/LoginPage.tsx"),
      doc("main", "Buju.tsx", "Buju.tsx", "src/components/Buju.tsx"),
      doc("main", "App.tsx", "App.tsx", "src/App.tsx"),
      doc("main", "api.ts", "api.ts", "src/api.ts"),
    ],
  },
];

export function findModule(id: string): SystemModule {
  return SYSTEM_MODULES.find((item) => item.id === id) ?? SYSTEM_MODULES[0];
}

/** 在源码里按点击文案定位，命中返回行号（1 起），否则返回 null。 */
export function locateLine(code: string, needle: string): number | null {
  const text = needle.trim();
  if (text.length < 2) return null;
  const lines = code.split("\n");
  const direct = lines.findIndex((line) => line.includes(text));
  if (direct >= 0) return direct + 1;
  const compact = text.replace(/\s+/g, "");
  const loose = lines.findIndex((line) => line.replace(/\s+/g, "").includes(compact));
  return loose >= 0 ? loose + 1 : null;
}

export type DocHit = { doc: SourceDoc; line: number; text: string; loose: boolean };

/** 在一组已加载的源文件里找文案：先找完全匹配，再找去空白后的匹配。 */
export function locateAcross(docs: { doc: SourceDoc; code: string }[], needle: string): DocHit | null {
  const text = needle.trim();
  if (text.length < 2) return null;
  const compact = text.replace(/\s+/g, "");

  for (const item of docs) {
    const rows = item.code.split("\n");
    const line = rows.findIndex((row) => row.includes(text));
    if (line >= 0) return { doc: item.doc, line: line + 1, text: rows[line].trim().slice(0, 120), loose: false };
  }
  for (const item of docs) {
    const rows = item.code.split("\n");
    const line = rows.findIndex((row) => row.replace(/\s+/g, "").includes(compact));
    if (line >= 0) return { doc: item.doc, line: line + 1, text: rows[line].trim().slice(0, 120), loose: true };
  }
  return null;
}

/** 只取命中行附近的一段代码，避免一屏全是 import 看不懂 */
export function focusWindow(code: string, line: number, radius = 10): { code: string; start: number; total: number } {
  const rows = code.split("\n");
  const start = Math.max(1, line - radius);
  const end = Math.min(rows.length, line + radius);
  return { code: rows.slice(start - 1, end).join("\n"), start, total: rows.length };
}
